import { useMemo } from "react";
import {
    QueryClient,
    keepPreviousData,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";

import {
    MeditationTracksAPI,
    uploadMeditationAudio,
} from "../infrastructure/api/MeditationTracks.api";
import {
    MeditationLikeState,
    MeditationTrack,
    MeditationTrackFilters,
    TRACKS_PAGE_SIZE,
} from "../domain/types";

const KEY = "meditation-tracks";
const CENTER_KEY = "meditation-tracks-center";
const PUBLIC_KEY = "meditation-tracks-public";

/** The therapist's own background music, newest first. */
export const useMeditationTracks = () =>
    useQuery({
        queryKey: [KEY],
        queryFn: async () => {
            const { data } = await MeditationTracksAPI.list();
            return data;
        },
    });

/** Their centre's shelf. Empty for a therapist in none. */
export const useCenterMeditationTracks = () =>
    useQuery({
        queryKey: [CENTER_KEY],
        queryFn: async () => {
            const { data } = await MeditationTracksAPI.listCenter();
            return data;
        },
    });

/**
 * One page of the public catalogue of music, numbered from 1, ten tracks as
 * the meditations are. One more is asked for, to know whether another page
 * follows without counting the whole catalogue.
 */
export const usePublicMeditationTracks = (
    enabled = true,
    filters: MeditationTrackFilters = {},
    page = 1
) => {
    const query = useQuery({
        queryKey: [PUBLIC_KEY, filters, page],
        queryFn: async () => {
            const { data } = await MeditationTracksAPI.listPublic(filters, {
                offset: (page - 1) * TRACKS_PAGE_SIZE,
                limit: TRACKS_PAGE_SIZE + 1,
            });
            return data;
        },
        // The page being left stays on screen until the next one arrives.
        placeholderData: keepPreviousData,
        enabled,
    });
    const data = useMemo(() => query.data?.slice(0, TRACKS_PAGE_SIZE), [query.data]);
    return { ...query, data, hasNext: (query.data?.length ?? 0) > TRACKS_PAGE_SIZE };
};

const invalidateTracks = (queryClient: ReturnType<typeof useQueryClient>) => {
    queryClient.invalidateQueries({ queryKey: [KEY] });
    queryClient.invalidateQueries({ queryKey: [CENTER_KEY] });
    queryClient.invalidateQueries({ queryKey: [PUBLIC_KEY] });
};

/** Put one of their own tracks in the public catalogue, or take it out. */
export const useSetTrackVisibility = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            id,
            isPublic,
            authorName,
        }: {
            id: string;
            isPublic: boolean;
            /** The name it is shared under. Required to share. */
            authorName?: string;
        }) => {
            const { data } = await MeditationTracksAPI.setVisibility(id, {
                public: isPublic,
                // Asked for on screen before this is sent; the API refuses to
                // share without it.
                rights_confirmed: isPublic || undefined,
                author_name: isPublic ? authorName : undefined,
            });
            return data;
        },
        onSuccess: () => invalidateTracks(queryClient),
    });
};

/** Put someone else's shared track on the caller's own shelf. */
export const useCopyMeditationTrack = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const { data } = await MeditationTracksAPI.copy(id);
            return data;
        },
        onSuccess: () => invalidateTracks(queryClient),
    });
};

/** Put a heart's new state on the track wherever a shelf lists it. */
const setLike = (queryClient: QueryClient, id: string, state: MeditationLikeState) => {
    for (const key of [KEY, CENTER_KEY, PUBLIC_KEY]) {
        const update = (list: MeditationTrack[]) =>
            list.map((t) => (t.id === id ? { ...t, ...state } : t));
        queryClient.setQueriesData<MeditationTrack[]>({ queryKey: [key] }, (cached) =>
            Array.isArray(cached) ? update(cached) : cached
        );
    }
};

/** Like shared music, or take the like back. Moves at once; the server settles it. */
export const useToggleTrackLike = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (track: MeditationTrack) => {
            const { data } = track.liked_by_me
                ? await MeditationTracksAPI.unlike(track.id)
                : await MeditationTracksAPI.like(track.id);
            return data;
        },
        onMutate: async (track) => {
            // A refetch landing after this would put the old heart back.
            await Promise.all(
                [KEY, CENTER_KEY, PUBLIC_KEY].map((key) =>
                    queryClient.cancelQueries({ queryKey: [key] })
                )
            );
            const liked = !track.liked_by_me;
            const count = Math.max(0, (track.like_count ?? 0) + (liked ? 1 : -1));
            setLike(queryClient, track.id, { like_count: count, liked_by_me: liked });
        },
        onSuccess: (state, track) => setLike(queryClient, track.id, state),
        onError: () => invalidateTracks(queryClient),
    });
};

/**
 * Adding a piece of music: ask for a slot, PUT the bytes, then register it.
 *
 * The same two-step upload a recording uses, for the same reason — the file
 * never passes through our API — with a third step that gives it a name and
 * the therapist's word that the music is theirs to use.
 */
export const useAddMeditationTrack = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            file,
            name,
            durationSeconds,
            forCenter = false,
        }: {
            file: File;
            name: string;
            durationSeconds?: number | null;
            forCenter?: boolean;
        }) => {
            const { data: slot } = await MeditationTracksAPI.uploadUrl(
                // Same reason as a recording's: a type with parameters on it
                // is not one the API has an extension for.
                file.type.split(";")[0].trim()
            );
            await uploadMeditationAudio(slot.upload_url, file);
            const { data } = await MeditationTracksAPI.create({
                name,
                audio_path: slot.audio_path,
                duration_seconds: durationSeconds ?? null,
                // Asked for in the form, sent here: the API refuses the track
                // without it.
                rights_confirmed: true,
                for_center: forCenter || undefined,
            });
            return data;
        },
        onSuccess: () => invalidateTracks(queryClient),
    });
};

/**
 * Keep the length a player measured, for a track uploaded before lengths were
 * kept. Best-effort: a track that is not the caller's own is simply refused.
 */
export const useRecordTrackDuration = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, seconds }: { id: string; seconds: number }) => {
            const { data } = await MeditationTracksAPI.recordDuration(id, seconds);
            return data;
        },
        onSuccess: () => invalidateTracks(queryClient),
    });
};

export const useRenameMeditationTrack = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, name }: { id: string; name: string }) => {
            const { data } = await MeditationTracksAPI.rename(id, name);
            return data;
        },
        onSuccess: () => invalidateTracks(queryClient),
    });
};

export const useDeleteMeditationTrack = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => MeditationTracksAPI.remove(id),
        onSuccess: () => invalidateTracks(queryClient),
    });
};

/**
 * A URL for playing one, since the file is not public.
 *
 * The bytes come through our API and become a blob URL: the bucket answers no
 * browser directly, and the session has to capture what it plays, which a
 * cross-origin element cannot be.
 */
export const trackPlaybackUrl = async (id: string): Promise<string> => {
    const { data } = await MeditationTracksAPI.audio(id);
    return URL.createObjectURL(data);
};
